import { useId } from "react";
import { AlertCircle } from "lucide-react";
import { Dica } from "./Dica";

/*
 * Campo de arquivo, na linha de `Campo`: rótulo, dica e erro, sem texto de apoio. Formato e
 * tamanho vão na `dica`, porque um parágrafo fixo sob o campo é lido uma vez e vira ruído.
 *
 * O `input` é o nativo, com a aparência no `file:`: teclado, leitor de tela e arrastar vêm de
 * graça. É não controlado por natureza, e o arquivo vive no estado de quem usa. O nome que
 * aparece é o do navegador: nada aqui o guarda.
 */
interface Props {
  rotulo: string;
  /** Regra que o rótulo não diz sozinho, em tooltip. */
  dica?: string | undefined;
  erro?: string | undefined;
  /** O filtro do seletor do sistema. Ajuda, e não valida: quem confere é `aoEscolher`. */
  accept: string;
  /** Devolve `false` para recusar: o campo volta a vazio, sem mostrar o arquivo recusado. */
  aoEscolher: (arquivo: File | null) => boolean;
}

export function CampoDeArquivo({ rotulo, dica, erro, accept, aoEscolher }: Props) {
  const id = useId();
  const idErro = `${id}-erro`;

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex items-center gap-1">
        <label htmlFor={id} className="rotulo">
          {rotulo}
        </label>
        {dica !== undefined && <Dica texto={dica} />}
      </div>

      <input
        id={id}
        type="file"
        accept={accept}
        aria-invalid={erro !== undefined}
        {...(erro !== undefined ? { "aria-describedby": idErro } : {})}
        onChange={(evento) => {
          const aceito = aoEscolher(evento.target.files?.[0] ?? null);
          if (!aceito) evento.target.value = "";
        }}
        className={[
          "h-9 w-full min-w-0 cursor-pointer overflow-hidden rounded-controle border bg-superficie-2 text-corpo text-texto-2",
          "file:mr-3 file:h-full file:cursor-pointer file:border-0 file:border-r file:border-solid file:border-fio",
          "file:bg-superficie file:px-3 file:text-corpo file:font-medium file:text-texto hover:file:bg-fio",
          erro ? "border-falha" : "border-fio hover:border-fio-forte",
        ].join(" ")}
      />

      {erro !== undefined && (
        <p id={idErro} role="alert" className="flex items-center gap-1 text-[12px] text-falha">
          <AlertCircle aria-hidden="true" className="size-3.5 shrink-0" />
          {erro}
        </p>
      )}
    </div>
  );
}
